import { ChangeDetectionStrategy, Component, Inject, inject, OnDestroy } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ModelForm } from '@shared/models';
import { BankFormData } from '@shared/models/bank.model';
import {
  getCurrentAccountInputs,
  currentAccountTableColumns,
} from '@shared/constants/current-account.constants';
import {
  createBankAccountForm,
  resetBankAccountForm,
  setupBankAccountFormBehavior,
} from '@shared/functions';
import { Subscription, first } from 'rxjs';
import { BankService } from '@shared/services/bank.service';
import { SelectData } from '@shared/models';
import { Column } from '@shared/components/simple-table/models/simple-table-model';
import { FormInput } from '@shared/models/form-input.model';
import { ZDialogComponent } from '../z-dialog/z-dialog.component';
import { WidgetDataListComponent } from '../widget-data-list/widget-data-list.component';
import { ZButtonComponent } from '../z-button/z-button.component';
import { TranslatePipe } from '@ngx-translate/core';

interface CurrentAccountDialogForm {
  bankAccounts: BankFormData[];
}

export interface CurrentAccountDialogData {
  title?: string;
  investorId?: number;
  investorName?: string;
  accounts?: BankFormData[];
  hiddenFields?: string[];
}

@Component({
    selector: 'app-current-account-dialog',
    templateUrl: './current-account-dialog.component.html',
    styleUrls: ['./current-account-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ZDialogComponent,
        WidgetDataListComponent,
        ZButtonComponent,
        TranslatePipe,
    ],
})
export class CurrentAccountDialogComponent implements OnDestroy {
  private readonly subscriptions = new Subscription();
  private readonly bankService = inject(BankService);

  // Somente a ID CORRETORA pode ser cadastrada: o campo Banco fica travado nesta opção.
  private static readonly LOCKED_BANK_LABEL = 'ID CORRETORA';
  private lockedBankOption: SelectData | null = null;

  formGroup: FormGroup<ModelForm<CurrentAccountDialogForm>> = new FormGroup({
    bankAccounts: new FormControl([] as BankFormData[], { nonNullable: true }),
  });

  bankAccountInputs: FormInput[] = getCurrentAccountInputs(this.bankService);
  bankAccountForm: FormGroup<ModelForm<BankFormData>> = createBankAccountForm();
  tableColumns: Column[] = [...currentAccountTableColumns];

  bankAccountFormDialog: { title: string; formGroup: FormGroup<ModelForm<BankFormData>>; inputs: FormInput[] } = {
    title: 'Conta Corrente',
    formGroup: this.bankAccountForm,
    inputs: this.bankAccountInputs,
  };

  constructor(
    public dialogRef: MatDialogRef<CurrentAccountDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CurrentAccountDialogData,
  ) {
    this.formGroup.controls.bankAccounts.setValue([...(data.accounts ?? [])]);

    if (data.hiddenFields?.length) {
      this.bankAccountInputs = this.bankAccountInputs.filter(
        (input) => !data.hiddenFields!.includes(input.formControlName ?? ''),
      );
      this.tableColumns = this.tableColumns.filter(
        (col) => !data.hiddenFields!.includes(col.id),
      );
      this.bankAccountFormDialog = {
        ...this.bankAccountFormDialog,
        inputs: this.bankAccountInputs,
      };
    }

    this.subscriptions.add(
      setupBankAccountFormBehavior(this.bankAccountForm, this.bankAccountInputs),
    );

    // Resolve a opção "ID CORRETORA" da lista real do backend e trava o campo Banco nela.
    this.subscriptions.add(
      this.bankService
        .getAsSelectOptions()
        .pipe(first())
        .subscribe((options) => {
          this.lockedBankOption =
            options.find((option) =>
              (option.label ?? '')
                .toUpperCase()
                .includes(CurrentAccountDialogComponent.LOCKED_BANK_LABEL),
            ) ?? null;
          this.lockBankField();
        }),
    );
  }

  lockBankField(): void {
    if (!this.lockedBankOption) return;
    const banco = this.bankAccountForm.controls.banco;
    banco.setValue(this.lockedBankOption, { emitEvent: false });
    banco.disable({ emitEvent: false });
  }

  resetBankAccountForm(): void {
    resetBankAccountForm(this.bankAccountForm);
    this.lockBankField();
  }

  save(): void {
    this.dialogRef.close(this.formGroup.controls.bankAccounts.getRawValue());
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}